import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useRouteMatch, withRouter } from 'react-router-dom';

import { AuthContext } from '../commons/auth/AuthContext';
import { withCookieAwareAuth } from '../commons/auth/withCookieAwareAuth';
import LoadingPage from '../commons/LoadingPage';
import { LoginForm } from '../commons/auth/LoginForm';
import ErrorPage from '../commons/ErrorPage';
import SessionExpired from '../commons/auth/SessionExpired';
import {
    getInvalidUrlMessageElement,
    icpInvoicingWorldEmailLink,
} from '../commons/MessageComponents';
import {
    authenticateInvoiceLink,
    reportIssueWithInvoicing,
    validateInvoiceLink,
    sessionExpiryCheck,
} from './invoicingRoutes';
import { INVOICE_LINK_STATUS } from './InvoicingConstants';
import {
    getUrlForInvoicingForm,
    getUrlForTrackInvoicing,
    VIEW_ROUTES_URL,
} from '../commons/UrlUtils';

const InvoicingDisplayContainer = (props) => {
    const { auth } = useContext(AuthContext);
    if (!auth.isAuthenticated) {
        return (
            <div className="mt-24">
                <LoginForm authenticateLink={authenticateInvoiceLink} />
            </div>
        );
    }

    const InvoiceDisplay = props.render;
    return (
        <div>
            {auth.isExpired && (
                <SessionExpired authenticateLink={authenticateInvoiceLink} />
            )}
            <InvoiceDisplay initialPageNumber={props.initialPageNumber} />
        </div>
    );
};

InvoicingDisplayContainer.propTypes = {
    render: PropTypes.element,
    authenticateLink: PropTypes.func,
    initialPageNumber: PropTypes.number,
};

const handleOnTaxDocumentSubmissionComplete = (props) => {
    // After tax document submission, navigate to page 2 (Tax Document page)
    if (props.setInitialPageNumber) {
        props.setInitialPageNumber(2);
    }
};

/**
 * Cookie-aware authentication container for invoicing.
 */
const CookieAwareAuthContainer = withCookieAwareAuth({
    validateSession: sessionExpiryCheck,
    onDigitalSignatureComplete: handleOnTaxDocumentSubmissionComplete,
    reportIssue: reportIssueWithInvoicing,
    emailLink: icpInvoicingWorldEmailLink,
    ChildComponent: InvoicingDisplayContainer,
});

const InvoicingAuthAwareContainer = (props) => {
    // when the user loads the form, it is unauthenticated.
    const [auth, setAuth] = useState({ isAuthenticated: false });
    const [initialPageNumber, setInitialPageNumber] = useState(1);

    return (
        <AuthContext.Provider value={{ auth, setAuth }}>
            <CookieAwareAuthContainer
                {...props}
                initialPageNumber={initialPageNumber}
                setInitialPageNumber={setInitialPageNumber}
            />
        </AuthContext.Provider>
    );
};

// TODO - why mix hooks with withRouter
const InvoicingAuthenticateContainer = withRouter((props) => {
    const [linkStatus, setLinkStatus] = useState();
    const routeMatch = useRouteMatch();
    const history = useHistory();

    const { url } = routeMatch.params;
    const { path } = props.match;
    const { state } = props.location;

    const setInvoiceUrlStatusDetails = (urlStatusDetails) => {
        setLinkStatus(urlStatusDetails.status);
        props.setConsultantCountry(urlStatusDetails.country);
    };

    // if the redirectAfterInvoiceSubmission prop is present in state, it implies that the redirection
    // is happening after successfully submitting the invoice details - updating the linkStatus accordingly
    if (
        state?.redirectAfterInvoiceSubmission &&
        linkStatus !== INVOICE_LINK_STATUS.SUBMITTED
    ) {
        setLinkStatus(INVOICE_LINK_STATUS.SUBMITTED);
    }

    // validate link when it changes.
    useEffect(() => {
        validateInvoiceLink(url, setInvoiceUrlStatusDetails);
    }, [url]);

    switch (linkStatus) {
        case INVOICE_LINK_STATUS.VALID:
            if (path !== VIEW_ROUTES_URL.INVOICING_FORM) {
                // TODO - what's the point of re-pushing the url, didn't we verify the pattern as
                // part of router.
                history.push(getUrlForInvoicingForm(url));
            }
            return <InvoicingAuthAwareContainer {...props} />;
        case INVOICE_LINK_STATUS.SUBMITTED:
            if (path !== VIEW_ROUTES_URL.TRACK_INVOICING) {
                history.push(getUrlForTrackInvoicing(url));
            }
            return <InvoicingAuthAwareContainer {...props} />;
        case INVOICE_LINK_STATUS.INVALID:
            return (
                <ErrorPage
                    message={'Invalid URL'}
                    subMessage={getInvalidUrlMessageElement(
                        icpInvoicingWorldEmailLink
                    )}
                />
            );
        case INVOICE_LINK_STATUS.EXPIRED:
            return (
                <ErrorPage
                    message={'The link you entered has expired.'}
                    subMessage={getInvalidUrlMessageElement(
                        icpInvoicingWorldEmailLink
                    )}
                />
            );
        default:
            return <LoadingPage />;
    }
});

InvoicingAuthenticateContainer.propTypes = {
    setConsultantCountry: PropTypes.func,
};

export default InvoicingAuthenticateContainer;
